import React, { useEffect } from 'react'
import i18n from 'constants/strings'
import { Redirect, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import BtnPrimary from 'components/generic/BtnPrimary'
import WatchlistPlayersTable from './WatchlistPlayersTable'
import { PageUrls } from './WatchListsRouter'
import styles from './watchlist-detail.module.scss'
import { RouterLink } from 'components/generic/router/links'
import { TABLET_BREAKPOINT, createRelPath } from 'constants/global'
import useWindowSize from 'hooks/use-windowSize'
import WatchlistOptionsMenu from './WatchlistOptionsMenu'
import { AppState } from 'redux/reducers'
import WatchlistStats from './common/WatchlistStats'
import { getWatchlistPlayers } from 'actions/watchlist-actions'
import LoadingSpinnerWithContainer from 'components/generic/LoadingSpinnerWithContainer'
import EmptyWatchlistPanel from './common/EmptyWatchlistPanel'
import { ReactComponent as IconUserWatchlist } from 'images/icons/icn_large_user_watchlist.svg'

const WatchlistDetail = () => {
  const dispatch = useDispatch()
  const { id }: { id: string } = useParams()
  const watchlist = useSelector((state: AppState) => state.watchlists.watchlists[id])
  const loadingWatchlists = useSelector((state: AppState) => state.watchlists.loadingWatchlists)
  const { avgSinglesWtn, minSinglesWtn, maxSinglesWtn } = watchlist?.stats ?? {}
  const windowWidth = useWindowSize().width
  const isMobile = windowWidth < TABLET_BREAKPOINT
  const addPlayersUrl = createRelPath('PLAYER_SEARCH', {
    queryParams: { 'destination-watchlist-id': id },
  })
  const resolvedPlayers = useSelector(
    (state: AppState) => (watchlist?.id && state.watchlists.watchlistPlayers[watchlist.id]) || []
  )
  const { resolvingPlayers } = useSelector((state: AppState) => state.watchlists)
  const hasPlayers = !!resolvedPlayers.length

  useEffect(() => {
    if (watchlist) dispatch(getWatchlistPlayers(watchlist))
  }, [watchlist, dispatch])

  if (!loadingWatchlists && !watchlist) {
    return <Redirect push to={{ pathname: PageUrls.WatchLists }} />
  }

  return (
    <>
      <RouterLink type='backLink' to={PageUrls.WatchLists} className={styles.mb32}>
        {i18n.t('strCommonUI.link.backTo', { name: i18n.t('strWatchlists.overview.title') })}
      </RouterLink>
      {!watchlist ? (
        <LoadingSpinnerWithContainer containerHeight={500} spinnerWidth={90} />
      ) : (
        <>
          <header className={styles.header}>
            <h1>{watchlist.name}</h1>
            <section className={styles.actionBtnsContainer}>
              {!isMobile && (
                <>
                  <BtnPrimary
                    href={addPlayersUrl}
                    className={styles.addBtnDesktop}
                    border={false}
                    size='medium'
                  >
                    {i18n.t('strWatchlists.detail.addBtn')}
                  </BtnPrimary>
                  <span aria-hidden />
                </>
              )}
              <WatchlistOptionsMenu id={watchlist.id} />
            </section>
          </header>

          <WatchlistStats
            totalPlayers={watchlist.playerIds.length}
            avgSinglesWtn={avgSinglesWtn}
            minSinglesWtn={minSinglesWtn}
            maxSinglesWtn={maxSinglesWtn}
            className={styles.statsContainer}
          />
          {isMobile && hasPlayers && (
            <BtnPrimary href={addPlayersUrl} className={styles.addBtnMobile} size='small'>
              {i18n.t('strWatchlists.detail.addBtn')}
            </BtnPrimary>
          )}
          {resolvingPlayers && !hasPlayers ? (
            <LoadingSpinnerWithContainer containerHeight={500} spinnerWidth={90} />
          ) : hasPlayers ? (
            <WatchlistPlayersTable players={resolvedPlayers} />
          ) : (
            <EmptyWatchlistPanel
              icon={<IconUserWatchlist aria-hidden />}
              title={i18n.t('strWatchlists.detail.panelEmpty.title')}
              btnUrl={addPlayersUrl}
            />
          )}
        </>
      )}
    </>
  )
}

export default WatchlistDetail
